import { Fragment } from 'react';
import { Button } from 'antd';
import { Link } from 'react-router';
import {
  recordTemplateDecisionSchema,
  type RecordTemplateDecisionDto,
  type TemplateDecisionRowDto,
} from '@pams/shared';
import { istDateTime } from '@/lib/analyticsReport';
import { templateHref } from '@/lib/templateLinks';
import { DECISIONS_COPY } from '../copy';
import { choiceWords, type DecisionLookups } from './choiceWords';
import { DecisionContext } from './DecisionContext';
import { isOneTemplateClass, usedByKeysOf } from './decisionContext';
import { DecisionForm, type DecisionFormProps } from './DecisionForm';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §3] ONE DECISION ROW — the code, how many templates use it, what the booklet says about it,
 *  the suggestion, the decision as it stands (by rule, or by whom), and whatever no longer holds.
 *
 *  ⚠️ "ACCEPT THE SUGGESTION" SENDS THE SUGGESTION AS IT IS — parsed first by the shared schema, so a
 *  suggestion the server would refuse is never offered as one click. Every other change opens the
 *  class's own form, inline.
 *
 *  [amended TEMPLATE-FIND] EVERY TEMPLATE NUMBER ON A ROW IS A LINK — the row named its templates as plain
 *  text and nothing on the page went anywhere, so a person reading `t152` had no way to find it. This
 *  reverses [P7C §4]'s "this page is unchanged" for the row, by the user's ask.
 *    · A class shared by many templates says "Used by N templates (t1, t3, …)", each number a link to that
 *      template through the library's own `?t=` (`templateHref`), the rest past those the server names as
 *      "and N more" — text, because no list of them exists to link.
 *    · A printed total and a duty point are about ONE place in ONE template, so their row has NO "Used by"
 *      line: its one link is `DecisionContext`'s "Show it in template t152", to that check or that row. A
 *      second link to the same template would reach one destination two ways (`NameCell`'s rule).
 *    · The heading stays `row.label`, as text; `scrollRef` stays on the `<li>`, so a `?code=` link still
 *      scrolls the row it asked for and nothing else.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const DecisionRow = ({
  row,
  setKey,
  lookups,
  open,
  onOpen,
  onClose,
  saving,
  onSave,
  form,
  scrollRef,
}: {
  row: TemplateDecisionRowDto;
  /** [TEMPLATE-FIND] The set the row's template numbers link into. */
  setKey: string;
  lookups: DecisionLookups;
  /** [P7B.1] Set on the row a link asked for (`?code=`), so it scrolls into view once, when it mounts. */
  scrollRef?: ((node: HTMLLIElement | null) => void) | undefined;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  saving: boolean;
  onSave: (body: RecordTemplateDecisionDto) => void;
  form: Omit<DecisionFormProps, 'row' | 'saving' | 'onSave' | 'onCancel'>;
}): React.ReactElement => {
  const suggestionBody =
    row.suggestion === null
      ? null
      : recordTemplateDecisionSchema.safeParse({ class: row.class, code: row.code, decision: row.suggestion });
  const decided = row.decision;
  const usedBy = usedByKeysOf(row);

  return (
    <li
      ref={scrollRef}
      className="flex flex-col gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm"
      data-testid="decision-row"
      data-code={row.code}
      data-decided={decided === null ? 'false' : 'true'}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="font-medium text-heading">{row.label}</span>
        {isOneTemplateClass(row.class) ? null : (
          <span className="text-xs text-muted-foreground" data-testid="decision-used-by">
            {DECISIONS_COPY.usedBy(row.usedBy)}
            {usedBy.shown.length > 0 ? (
              <>
                {' ('}
                {usedBy.shown.map((key, index) => (
                  <Fragment key={key}>
                    {index > 0 ? ', ' : ''}
                    <Link
                      to={templateHref(setKey, key)}
                      className="text-link underline-offset-4 hover:underline"
                      aria-label={DECISIONS_COPY.openTemplate(key)}
                      data-testid="decision-template-link"
                    >
                      {key}
                    </Link>
                  </Fragment>
                ))}
                {usedBy.more > 0 ? ` ${DECISIONS_COPY.usedByMore(usedBy.more)}` : ''}
                {')'}
              </>
            ) : null}
          </span>
        )}
      </div>
      <DecisionContext row={row} setKey={setKey} />
      {row.suggestion === null ? null : (
        <p data-testid="decision-suggestion">
          <span className="text-muted-foreground">{DECISIONS_COPY.suggestion}: </span>
          {choiceWords(row.class, row.suggestion, row.context, lookups)}
        </p>
      )}
      <p data-testid="decision-current">
        {decided === null ? (
          <span className="text-muted-foreground">{DECISIONS_COPY.outstanding}</span>
        ) : (
          <>
            <span className="text-muted-foreground">{DECISIONS_COPY.decided}: </span>
            {choiceWords(row.class, decided.choice, row.context, lookups)}
            <span className="text-xs text-muted-foreground">
              {' — '}
              {decided.byRule
                ? DECISIONS_COPY.byRule
                : decided.decidedBy
                  ? DECISIONS_COPY.by(decided.decidedBy.name, decided.decidedAt ? istDateTime(decided.decidedAt) : null)
                  : ''}
            </span>
            {decided.note ? <span className="block text-xs text-muted-foreground">{decided.note}</span> : null}
          </>
        )}
      </p>
      {row.problems.length > 0 ? (
        <div className="flex flex-col gap-0.5" data-testid="decision-problems">
          <span className="font-medium">{DECISIONS_COPY.problems}</span>
          {row.problems.map((problem) => (
            <span key={problem} className="text-muted-foreground">
              {problem}
            </span>
          ))}
        </div>
      ) : null}
      {open ? (
        <DecisionForm {...form} row={row} saving={saving} onSave={onSave} onCancel={onClose} />
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button size="small" onClick={onOpen} data-testid="decision-open">
            {decided === null ? DECISIONS_COPY.decide : DECISIONS_COPY.change}
          </Button>
          {decided === null && suggestionBody?.success ? (
            <Button
              size="small"
              type="primary"
              loading={saving}
              data-testid="decision-accept-suggestion"
              onClick={() => {
                onSave(suggestionBody.data);
              }}
            >
              {DECISIONS_COPY.useSuggestion}
            </Button>
          ) : null}
        </div>
      )}
    </li>
  );
};
